//
// layerHide.ts — live Mapbox-layer-visibility override.
//
// The panel lets the user tick which style layers to hide in the
// rendered video. Without a live override the host app's map keeps
// showing those layers until render time, so the user can't see
// the effect of their picks in advance.
//
// A LayerHideController wraps a map and installs two monkey-
// patches — on setLayoutProperty and addLayer — that enforce its
// hidden set against host-app mutations (typically react-map-gl
// declarative re-syncs). For each id it hides, it captures the
// layer's *original* visibility at the moment it was first hidden,
// so that removing an id from the hidden set (or tearing down the
// whole controller) can restore the layer to its pre-override
// state.
//
// The controller attaches itself to the map as `__mve_layer_
// controller` so the render path can detect it: if a controller
// is live, render's applyHiddenLayers short-circuits (the layers
// are already hidden). Without detection, render's own apply +
// restore would stack on top and behave correctly but redundantly.
//

import type { Map as MapboxMap } from 'mapbox-gl';

export interface LayerHideController {
  //
  // Replace the current hidden set with these ids. Hides newly-
  // added ids on the live map and restores newly-removed ones.
  // Safe to call repeatedly; unknown ids are skipped with a
  // console warning so a typo in a scenario doesn't crash.
  //
  setHidden(ids: string[]): void;
  //
  // Fully restore every layer we've touched and uninstall the
  // monkey-patches. After this the controller is dead; call
  // createLayerHideController again for a fresh one.
  //
  teardown(): void;
}

interface PatchTarget {
  setLayoutProperty: (layerId: string, name: string, value: unknown, options?: unknown) => unknown;
  addLayer: (layer: unknown, beforeId?: string) => unknown;
}

//
// Attach a fresh controller to the given map. Idempotent in one
// direction: if a controller is already attached, the existing one
// is returned so the caller doesn't accidentally create two stacks
// of patches on the same map. Tearing down and creating again is
// fine — the teardown clears the attach slot.
//
export function createLayerHideController(map: MapboxMap): LayerHideController {
  const existing = getActiveController(map);
  if (existing) return existing;

  const mapAny = map as unknown as PatchTarget;
  const originalSetLayoutProperty = mapAny.setLayoutProperty;
  const originalAddLayer = mapAny.addLayer;

  const hiddenSet = new Set<string>();
  const originals = new Map<string, 'visible' | 'none'>();

  mapAny.setLayoutProperty = function patched(
    this: unknown,
    layerId: string,
    name: string,
    value: unknown,
    options?: unknown,
  ): unknown {
    if (name === 'visibility' && hiddenSet.has(layerId) && value !== 'none') {
      return this;
    }
    return originalSetLayoutProperty.call(this, layerId, name, value, options);
  };

  mapAny.addLayer = function patchedAdd(this: unknown, layer: unknown, beforeId?: string): unknown {
    if (layer && typeof layer === 'object' && 'id' in layer) {
      const id = (layer as { id: string }).id;
      if (hiddenSet.has(id)) {
        const layout = (layer as { layout?: Record<string, unknown> }).layout ?? {};
        const patchedLayer = { ...(layer as object), layout: { ...layout, visibility: 'none' } };
        return originalAddLayer.call(this, patchedLayer, beforeId);
      }
    }
    return originalAddLayer.call(this, layer, beforeId);
  };

  const hideLayer = (id: string): void => {
    try {
      const prev = map.getLayoutProperty(id, 'visibility');
      originals.set(id, prev === 'none' ? 'none' : 'visible');
      hiddenSet.add(id);
      // Call through the original so our own patch doesn't block
      // our own intent.
      originalSetLayoutProperty.call(map, id, 'visibility', 'none');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn(`[mve] ${id}: not found in style (${message})`);
    }
  };

  const showLayer = (id: string): void => {
    const prev = originals.get(id);
    if (prev === undefined) return;
    hiddenSet.delete(id);
    originals.delete(id);
    try {
      originalSetLayoutProperty.call(map, id, 'visibility', prev);
    } catch {
      // Layer vanished from the style between hide and show.
    }
  };

  const setHidden = (ids: string[]): void => {
    const next = new Set(ids);
    for (const id of Array.from(hiddenSet)) {
      if (!next.has(id)) showLayer(id);
    }
    for (const id of ids) {
      if (!hiddenSet.has(id)) hideLayer(id);
    }
  };

  const controller: LayerHideController = {
    setHidden,
    teardown: () => {
      for (const id of Array.from(hiddenSet)) showLayer(id);
      mapAny.setLayoutProperty = originalSetLayoutProperty;
      mapAny.addLayer = originalAddLayer;
      delete (map as unknown as { __mve_layer_controller?: LayerHideController })
        .__mve_layer_controller;
    },
  };

  (map as unknown as { __mve_layer_controller?: LayerHideController })
    .__mve_layer_controller = controller;
  return controller;
}

//
// Look up the live controller a map may already have attached.
// Used by the render path to detect that its own apply/restore
// would be redundant and skip it.
//
export function getActiveController(map: MapboxMap): LayerHideController | null {
  return (map as unknown as { __mve_layer_controller?: LayerHideController })
    .__mve_layer_controller ?? null;
}

//
// Per-layer-type mapping from Mapbox layer type to the paint
// property name(s) that control its opacity. Symbol layers split
// their opacity across text and icon, circle layers across fill
// and stroke; we fade them all in lockstep so the layer behaves
// as one visual unit.
//
// Types NOT in this table (hillshade, sky, custom) have no
// opacity-like paint property we can drive, so fades on those
// layers are ignored with a warning at apply time.
//
const OPACITY_PAINT_PROPS: Record<string, string[]> = {
  background: ['background-opacity'],
  raster: ['raster-opacity'],
  line: ['line-opacity'],
  fill: ['fill-opacity'],
  'fill-extrusion': ['fill-extrusion-opacity'],
  symbol: ['text-opacity', 'icon-opacity'],
  circle: ['circle-opacity', 'circle-stroke-opacity'],
  heatmap: ['heatmap-opacity'],
};

//
// Captured original-opacity info for one layer the fade controller
// has touched. Stored lazily on first setOpacityFactors call for
// that layer so the fade multiplies against the user's actual
// styled opacity (e.g. a raster-opacity of 0.8 in the style fades
// to 0 and back to 0.8, not to 1).
//
interface FadeOriginalEntry {
  type: string;
  // Per paint-property name, the value we read from the style. If
  // a property was unset (undefined), we treat its default as 1 and
  // restore by setting `undefined` to clear any override we wrote.
  originals: Record<string, number | undefined>;
}

//
// Live opacity-fade override. Tracks, per layer it has been asked
// to fade, the layer's *original* paint opacity across each
// relevant paint property. Calling setOpacityFactors applies
// `factor × original` to each property; teardown restores
// everything to its captured original.
//
export interface LayerFadeController {
  //
  // Set the fade factor (in [0, 1]) for each layer in `factors`.
  // Layers previously faded but not present in this call are
  // restored to factor 1 (i.e. their original style opacity).
  //
  setOpacityFactors(factors: Record<string, number>): void;
  //
  // Restore every layer we've touched to its captured original
  // paint opacity and detach. After this the controller is dead.
  //
  teardown(): void;
}

//
// Attach a fresh fade controller to the map. Idempotent: if one
// already exists, returns the existing instance so we don't stack
// two sets of original-opacity trackers on the same map.
//
export function createLayerFadeController(map: MapboxMap): LayerFadeController {
  const existing = getActiveFadeController(map);
  if (existing) return existing;

  const originals = new Map<string, FadeOriginalEntry>();
  // Memo of the last factor we wrote per layer, so we skip
  // redundant setPaintProperty calls. setPaintProperty triggers a
  // re-render each call — avoiding it on no-op updates keeps the
  // scrub loop cheap.
  const lastFactor = new Map<string, number>();

  const getLayerType = (layerId: string): string | null => {
    try {
      const layer = (map as unknown as { getLayer?: (id: string) => { type: string } | undefined })
        .getLayer?.(layerId);
      return layer?.type ?? null;
    } catch {
      return null;
    }
  };

  const ensureOriginals = (layerId: string): FadeOriginalEntry | null => {
    const cached = originals.get(layerId);
    if (cached) return cached;
    const type = getLayerType(layerId);
    if (!type) {
      console.warn(`[mve] fade: layer ${layerId} not found in style`);
      return null;
    }
    const props = OPACITY_PAINT_PROPS[type];
    if (!props) {
      console.warn(`[mve] fade: layer type '${type}' has no opacity property (layer ${layerId})`);
      const entry: FadeOriginalEntry = { type, originals: {} };
      originals.set(layerId, entry);
      return entry;
    }
    const entry: FadeOriginalEntry = { type, originals: {} };
    for (const prop of props) {
      try {
        const raw = (map as unknown as {
          getPaintProperty: (id: string, name: string) => unknown;
        }).getPaintProperty(layerId, prop);
        entry.originals[prop] = typeof raw === 'number' ? raw : undefined;
      } catch {
        entry.originals[prop] = undefined;
      }
    }
    originals.set(layerId, entry);
    return entry;
  };

  const applyLayer = (layerId: string, factor: number): void => {
    if (lastFactor.get(layerId) === factor) return;
    lastFactor.set(layerId, factor);
    const entry = originals.get(layerId);
    if (!entry) return;
    const setPaint = (map as unknown as {
      setPaintProperty: (id: string, name: string, value: unknown) => unknown;
    }).setPaintProperty;
    for (const [prop, orig] of Object.entries(entry.originals)) {
      // A layer with no opacity-capable paint property (e.g.
      // hillshade) has an empty originals map; the inner loop
      // doesn't execute. We've still registered the layer in
      // `originals` so we don't spam warnings on every frame.
      const target = (orig ?? 1) * factor;
      try {
        setPaint.call(map, layerId, prop, target);
      } catch {
        // Layer removed from style mid-fade; next ensureOriginals
        // call will log.
      }
    }
  };

  const setOpacityFactors = (factors: Record<string, number>): void => {
    // Restore any previously-faded layer not in the new set to
    // factor 1 (fully visible at original opacity).
    for (const layerId of Array.from(lastFactor.keys())) {
      if (!(layerId in factors)) applyLayer(layerId, 1);
    }
    for (const [layerId, factor] of Object.entries(factors)) {
      ensureOriginals(layerId);
      applyLayer(layerId, Math.max(0, Math.min(1, factor)));
    }
  };

  const teardown = (): void => {
    const setPaint = (map as unknown as {
      setPaintProperty: (id: string, name: string, value: unknown) => unknown;
    }).setPaintProperty;
    for (const [layerId, entry] of originals) {
      for (const [prop, orig] of Object.entries(entry.originals)) {
        try {
          // Setting undefined clears our override and lets the
          // style's original value (or default) take over again.
          setPaint.call(map, layerId, prop, orig);
        } catch {
          // Layer gone — nothing we can do.
        }
      }
    }
    originals.clear();
    lastFactor.clear();
    delete (map as unknown as { __mve_layer_fade_controller?: LayerFadeController })
      .__mve_layer_fade_controller;
  };

  const controller: LayerFadeController = { setOpacityFactors, teardown };
  (map as unknown as { __mve_layer_fade_controller?: LayerFadeController })
    .__mve_layer_fade_controller = controller;
  return controller;
}

//
// Look up a live LayerFadeController attached to the map. Used by
// the render path to prefer the panel's controller (which already
// knows each layer's originals) over creating its own ephemeral
// one.
//
export function getActiveFadeController(map: MapboxMap): LayerFadeController | null {
  return (map as unknown as { __mve_layer_fade_controller?: LayerFadeController })
    .__mve_layer_fade_controller ?? null;
}
